import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { fetchServicesProviderAndPropertyDeveloperList } from 'src/services/client';

export interface ServiceProviderContact {
  id: string;
  firstname: string;
  lastname: string;
  [key: string]: any;
}

interface UseServiceProviderListReturn {
  serviceProviders: ServiceProviderContact[];
  isLoading: boolean;
  error: string | null;
}

export const fetchServiceProviderData = async (
  loginUserId: string,
  role: string
): Promise<ServiceProviderContact[]> => {
  const response: any = await fetchServicesProviderAndPropertyDeveloperList(
    loginUserId,
    role
  );

  if (!response || (!response.ok && response.status !== 200)) {
    throw new Error('Failed to fetch service provider list');
  }

  const responseJson: ServiceProviderContact[] = await response.json();
  return responseJson ?? [];
};

export const useServiceProviderList = (): UseServiceProviderListReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const [serviceProviders, setServiceProviders] = useState<
    ServiceProviderContact[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loginUserId: string = user?.id;
  const role: string = user?.roles?.[0]?.role?.name;

  const loadServiceProviders = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const result: ServiceProviderContact[] = await fetchServiceProviderData(
        loginUserId,
        role
      );
      setServiceProviders(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!loginUserId || !role) {
      return;
    }

    loadServiceProviders();
  }, [loginUserId, role]);

  return { serviceProviders, isLoading, error };
};

export default useServiceProviderList;
